<template>
  <main class="w-full place-items-center md:bg-[#f3f3f3] min-h-full" :id="routeName">
    <Header />
    <div class="container mx-auto py-24 mainpage-padding-top md:max-w-[900px] min-h-screen border-box justify-center items-center">
      <!-- Your page content will inject here -->
      <slot />
    </div>
  </main>
</template>

<script setup>
    import { onMounted, onBeforeUnmount } from 'vue';
    import { useRuntimeConfig } from 'nuxt/app';
    import { useCookieYes } from '~/composables/useCookieYes';
    import { useTracking } from '~/composables/useSocialMediaTracking';
    import { useBookingStore } from '@/stores/booking';

    const router = useRouter();
    const route = useRoute();
    const routeName = computed(() => router.currentRoute.value.name);

    const runtimeConfig = useRuntimeConfig();
    const bookingStore = useBookingStore();

    // Staff pages and staff journeys get no cookie banner or Meta pixel
    const isStaffPage = () =>
        /^\/(up-staff-internal-use|walk-in|enquiry-team)(\/|$)/.test(route.path) ||
        bookingStore.isStaffJourney();

    // Meta pixel for consented users, same as book-now
    let handleConsentUpdate = null;

    onMounted(async () => {
        if (isStaffPage()) return;

        const { loadCookieYes, checkConsent } = useCookieYes();
        const { initMetaPixel } = useTracking();
        const { metaPixelId } = runtimeConfig.public;

        await loadCookieYes();

        const loadTracking = () => {
            if (isStaffPage()) return;
            if (!checkConsent()) return;

            if (metaPixelId) {
                initMetaPixel(metaPixelId);
            }
        };

        const loadAfterPage = () => {
            if ('requestIdleCallback' in window) {
                window.requestIdleCallback(loadTracking, {
                    timeout: 3000,
                });
            } else {
                window.setTimeout(loadTracking, 3000);
            }
        };

        // Existing user: page has already loaded
        if (document.readyState === 'complete') {
            loadAfterPage();
        } else {
            window.addEventListener('load', loadAfterPage, {
                once: true,
            });
        }

        // New user accepts/updates consent, or returning user once getCkyConsent is ready
        handleConsentUpdate = () => {
            loadAfterPage();
        };

        document.addEventListener('cookieyes_consent_update', handleConsentUpdate);
        document.addEventListener('cookieyes_banner_load', handleConsentUpdate);
    });

    onBeforeUnmount(() => {
        if (!handleConsentUpdate) return;

        document.removeEventListener('cookieyes_consent_update', handleConsentUpdate);
        document.removeEventListener('cookieyes_banner_load', handleConsentUpdate);
    });
</script>
